<template>
  <div id="DataSourceAttributesEditingDialog">
    <main-large-dialog
      attach="#DataSourceAttributesEditingDialog"
      :is-visible.sync="computedIsVisible"
      :title="`${$i18n.t('dialog.manageDataSourceAttributes')} - ${editingDataSource.verbose_name}`"
      max-height="85vh"
      min-body-height="55vh"
      dialog-width="90%"
      header-shadow
      no-paddings-y
      no-paddings-x
      disagree-text="dialog.disagreeClose"
      :is-agree-visible="false"
      @change="$root.$emit('attributesChanged')"
    >
      <template #body>
        <template v-if="isLoaded">
          <v-col cols="12" style="height: 100%; display: flex; flex-direction: column">
            <v-row justify="end" align="center" no-gutters>
              <v-col cols="3" class="pr-5 py-4">
                <data-input
                  v-model="searchText"
                  :data-type="{
                    name: 'text',
                  }"
                  @input="handleSearch"
                  prepend-inner-icon="mdi-magnify"
                  :placeholder="$i18n.t('default.find')"
                  hide-details
                  clearable
                />
              </v-col>
            </v-row>
            <custom-table
              :table-props="{ style: 'height: 100%', itemsPerPage: 50 }"
              :headers="headers"
              :items="filteredAttributes"
            >
              <template #[`customtable-column-append`]="{ header }">
                <v-tooltip bottom color="font" v-if="header.info">
                  <template #activator="{ on }">
                    <v-icon v-on="on" class="pl-1" small :color="$_colors.primarylight">
                      mdi-information
                    </v-icon>
                  </template>
                  <span>
                    {{ $i18n.t(`${header.translatePath}Info`) }}
                  </span>
                </v-tooltip>
                <v-tooltip v-if="header.value === 'dict_id' && !isSqlDataSource" color="font" bottom>
                  <template #activator="{ on }">
                    <v-btn
                      v-on="on"
                      color="success"
                      :ripple="false"
                      width="auto"
                      depressed
                      icon
                      @click="openDictDialog"
                    >
                      <v-icon small> mdi-plus-circle </v-icon>
                    </v-btn>
                  </template>
                  <span> {{ $i18n.t('dialog.addNewDict') }} </span>
                </v-tooltip>
              </template>
              <template v-if="!isSqlDataSource" #[`customtable-header`]>
                <data-source-attributes-editing-table-row
                  is-new-attribute
                  :headers="headers"
                  :data-source="computedDataSource"
                  :is-visible="computedIsVisible"
                />
              </template>
              <template #[`customtable-row`]="{ data }">
                <data-source-attributes-editing-table-row
                  :attribute="data.row"
                  :headers="headers"
                  :index="data.index"
                  :data-source="computedDataSource"
                  :desc-attribute.sync="descAttribute"
                  @deleteAttribute="confirmAttributeDelete"
                />
              </template>
            </custom-table>
            <main-dialog
              v-if="isDeleteAttributeLoaded"
              :is-visible.sync="isDeleteAttributeVisible"
              :title="$i18n.t('dialog.deleteAttribute')"
              :body="$i18n.t('dialog.confirmAttributeDelete', { name: attributeToDelete.name })"
              agree-text="dialog.agreeDelete"
              @agree="handleAttributeDelete()"
            >
            </main-dialog
          ></v-col>
        </template>
        <template v-else>
          <main-loader class="pt-10" />
        </template>
      </template>
    </main-large-dialog>
    <dictionaries-editing
      v-if="isDictDialogLoaded"
      :is-visible.sync="isDictDialogVisible"
      :current-dict="{
        name: '',
        colors: [],
        pictures: [],
        picturesUrls: [],
        values: [],
        isNew: true,
        data_type: 'text',
      }"
    />
  </div>
</template>

<script>
import { get, call } from 'vuex-pathify';
import { debounce } from 'lodash';

import DataInput from '@/components/DataInput';
import MainLoader from '@/components/MainLoader';
import CustomTable from '@/components/CustomTable';
import DataSourceAttributesEditingTableRow from '@/components/DataSourceAttributesEditingTableRow';
import MainLargeDialog from '@/components/MainLargeDialog';

export default {
  name: 'DataSourceAttributesEditing',
  components: {
    MainDialog: () => import('@/components/MainDialog'),
    MainLargeDialog,
    MainLoader,
    CustomTable,
    DataInput,
    DataSourceAttributesEditingTableRow,
    DictionariesEditing: () => import('@/components/DictionariesEditing'),
  },
  props: {
    editingDataSource: {
      type: Object,
      required: true,
    },
    isVisible: {
      type: Boolean,
      required: false,
    },
    isDataSourceFromLayer: {
      type: Boolean,
      required: false,
    },
  },
  data: vm => ({
    attributeToDelete: {},
    isDeleteAttributeVisible: false,
    isDeleteAttributeLoaded: false,
    isLoaded: false,
    searchText: '',
    filteredAttributes: [],
    descAttribute: '',
    buttons: [
      {
        key: 'cancel',
        label: vm.$i18n.t('dialog.disagreeClose'),
        cancel: true,
      },
    ],
    isDictDialogLoaded: false,
    isDictDialogVisible: false,
  }),
  computed: {
    datasources: get('layers/metadata'),
    computedDescAttribute() {
      return this.datasources[this.computedDataSource.name]?.attributes_schema?.desc_attribute_name;
    },
    computedDataSource: {
      get() {
        return this.editingDataSource;
      },
      set(nV) {
        this.$emit('update:editingDataSource', nV);
      },
    },
    isSqlDataSource() {
      return this.computedDataSource?.datasource_type === 'sql_simple_local';
    },
    computedIsVisible: {
      get() {
        return this.isVisible;
      },
      set(nV) {
        this.$emit('update:isVisible', nV);
      },
    },
    headers() {
      const minWidth = '150px';
      const headers = [
        {
          value: 'verbose_name',
          translatePath: 'dialog.body.displayName',
          width: '12%',
          minWidth,
        },
        {
          value: 'name',
          translatePath: 'dialog.systemName',
          width: '12%',
          info: true,
          minWidth,
        },
        {
          value: 'data_type',
          translatePath: 'dialog.dataType',
          width: '12%',
          info: true,
          minWidth,
        },
        {
          value: 'desc_attribute_name',
          translatePath: 'dialog.descriptionName',
          width: '10%',
          info: true,
          minWidth: '100px',
        },
        {
          value: 'read_only',
          translatePath: 'dialog.readOnly',
          width: '10%',
          info: true,
          minWidth: '100px',
        },
        {
          value: 'dict_id',
          translatePath: 'dialog.dictionary',
          width: '12%',
          info: true,
          minWidth,
        },
        {
          value: 'relation',
          translatePath: 'dialog.relation',
          width: '12%',
          info: true,
          minWidth,
        },
        {
          value: 'measurement_unit_id',
          translatePath: 'dialog.units',
          width: '12%',
          info: true,
          minWidth,
        },
        {
          value: 'options',
          translatePath: 'dialog.options',
          width: '8%',
        },
      ];
      return this.isSqlDataSource ? headers.filter(hd => !['read_only', 'options'].includes(hd.value)) : headers;
    },
  },
  watch: {
    computedIsVisible(nV) {
      if (nV) {
        /**
         * Workaround for opening heavily-loaded modal without stuttering
         */
        setTimeout(() => {
          this.isLoaded = true;
        }, 200);
      } else {
        this.isLoaded = false;
        this.searchText = '';
      }
    },
  },
  methods: {
    getDicts: call('admin/getDictionaries'),
    getRelations: call('admin/getAllowedRelations'),
    getDataTypes: call('layers/getDataTypes'),
    getMeasurementUnits: call('layers/getMeasurementUnits'),
    deleteAttribute: call('layers/deleteAttribute'),
    editDataSource: call('layers/editDataSourceMetadata'),
    getLayerMetadata: call('layers/getLayerMetadata'),
    async setDescAttribute(attr) {
      const payload = {
        name: this.editingDataSource.name,
        body: {
          desc_attribute_name: attr,
          verbose_name: this.editingDataSource.verbose_name,
        },
      };
      try {
        await this.editDataSource(payload);
        await this.getLayerMetadata({ dataSource: this.editingDataSource.name });
      } catch (error) {
        console.log('error:', error);
      }
    },
    confirmAttributeDelete(attribute) {
      this.attributeToDelete = attribute;
      this.isDeleteAttributeLoaded = true;
      this.isDeleteAttributeVisible = true;
    },
    async handleAttributeDelete() {
      const attributeName = this.attributeToDelete.name;
      const payload = {
        attributeName,
        dataSourceName: this.computedDataSource.name,
      };
      await this.deleteAttribute(payload);
      this.$root.$emit('attributesChanged');
      const attributes = JSON.parse(JSON.stringify(this.computedDataSource.attributes_schema.attributes));
      const computedDataSource = JSON.parse(JSON.stringify(this.computedDataSource));
      computedDataSource.attributes_schema.attributes = attributes.filter(a => a.name !== attributeName);
      this.computedDataSource = computedDataSource;
      this.isDeleteAttributeVisible = false;
    },
    handleFilterAttributes() {
      this.filteredAttributes = this.searchText
        ? this.attributesList.filter(
            attr =>
              attr.name.toLowerCase().includes(this.searchText.toLowerCase()) ||
              attr.verbose_name.toLowerCase().includes(this.searchText.toLowerCase())
          )
        : this.attributesList;
    },
    async init() {
      await Promise.all([this.getDicts(), this.getRelations(), this.getDataTypes(), this.getMeasurementUnits()]);
      this.descAttribute = this.computedDescAttribute;
      this.$watch(
        vm => vm.descAttribute,
        nV => this.setDescAttribute(nV)
      );
      this.$watch(
        vm => vm.editingDataSource,
        () => {
          this.descAttribute = this.computedDescAttribute;
          const filteredAttributes =
            this.computedDataSource?.attributes_schema?.attributes
              .filter(attribute => attribute.data_type.name !== 'geometric' && attribute.name !== 'topogeom')
              .sort((a, b) => a.verbose_name.localeCompare(b.verbose_name)) || [];
          this.attributesList = JSON.parse(JSON.stringify(filteredAttributes));
          this.handleFilterAttributes();
        },
        {
          immediate: true,
        }
      );
      this.isLoaded = true;
    },
    openDictDialog() {
      this.isDictDialogLoaded = true;
      this.isDictDialogVisible = true;
    },
  },
  created() {
    this.handleSearch = debounce(this.handleFilterAttributes, 475);
    this.init();
  },
};
</script>

<style lang="scss" scoped>
::v-deep {
  .maindialog-body-card-text {
    display: flex !important;
    overflow: hidden !important;
  }
  .mainlargedialog-body-wrapper {
    width: 100% !important;
  }
  .v-input--radio-group,
  .v-input--radio-group .v-input__control,
  .v-input--radio-group .v-input__control .v-input__slot,
  .v-input--radio-group .v-input__control .v-input__slot .v-input--radio-group__input {
    height: 100% !important;
  }
}

.columnTitle {
  font-family: Roboto;
  font-style: normal;
  font-weight: 500;
  font-size: 13px;
  line-height: 16px;
}
.border-top {
  @include border(top, 1px);
}
.sticky-header {
  position: sticky;
  top: 0;
  background-color: #fff;
  z-index: 1;
  @include border(bottom, 1px);
}
::v-deep {
  .v-input {
    font-size: 12px;
  }
  .v-text-field.v-text-field--enclosed .v-text-field__details {
    margin-bottom: 0;
  }
}
.v-btn::before {
  background-color: transparent;
}
</style>
